import { defineStore } from "pinia";

type ColorMode = "system" | "dark" | "light";
type DisplayMode = "color" | "monochrome";

interface ThemeState {
  colorMode: ColorMode;
  displayMode: DisplayMode;
}

export const useThemeStore = defineStore("theme", {
  persist: {
    storage: localStorage,
  },

  state: (): ThemeState => ({
    colorMode: "system",
    displayMode: "color",
  }),

  getters: {
    systemPrefersDark(): boolean {
      return window.matchMedia("(prefers-color-scheme: dark)").matches;
    },

    activeTheme(): "dark" | "light" {
      return this.colorMode === "system" ? (this.systemPrefersDark ? "dark" : "light") : this.colorMode;
    },

    isDark(): boolean {
      return this.activeTheme === "dark";
    },

    isLight(): boolean {
      return this.activeTheme === "light";
    },

    isSystemMode(): boolean {
      return this.colorMode === "system";
    },

    isDarkMode(): boolean {
      return this.colorMode === "dark";
    },

    isLightMode(): boolean {
      return this.colorMode === "light";
    },

    isMonochrome(): boolean {
      return this.displayMode === "monochrome";
    },

    isColorMode(): boolean {
      return this.displayMode === "color";
    },
  },

  actions: {
    setColorMode(mode: ColorMode): void {
      this.colorMode = mode;
      this.applyTheme();
    },

    setDisplayMode(mode: DisplayMode): void {
      this.displayMode = mode;
      this.applyDisplayMode();
    },

    toggleColorMode(): void {
      if (this.colorMode !== "system") {
        this.setColorMode(this.colorMode === "dark" ? "light" : "dark");
      } else {
        this.setColorMode(this.systemPrefersDark ? "light" : "dark");
      }
    },

    toggleDisplayMode(): void {
      this.setDisplayMode(this.displayMode === "color" ? "monochrome" : "color");
    },

    applyTheme(): void {
      if (this.isDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    },

    applyDisplayMode(): void {
      if (this.isMonochrome) {
        document.body.classList.add("is-monochrome");
      } else {
        document.body.classList.remove("is-monochrome");
      }
    },

    applyAll(): void {
      this.applyTheme();
      this.applyDisplayMode();
    },

    init(): void {
      this.applyAll();

      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handleThemeChange = () => {
        if (this.colorMode === "system") {
          this.applyTheme();
        }
      };

      mediaQuery.addEventListener("change", handleThemeChange);
    },
  },
});
